import type { AppStore } from './store.js';

/**
 * Playback: one item at a time across the app, and only after Play is pressed (03 §9).
 * The state holds what is playing; a video or audio element follows it and reports its position back.
 * A song without an audio source (fixtures) advances one second per second, as in the prototype.
 *
 * Videos (owner, 4 October 2026) play muted on their own; the slot then says which one has its sound on.
 * Turning a video's sound on takes the slot, so a song stops, and a song or another video taking the slot
 * mutes it again. Every navigation clears the slot, which mutes them all.
 */

const ticks = new WeakMap<AppStore, ReturnType<typeof setInterval>>();

export function stopTick(s: AppStore): void {
  const tick = ticks.get(s);
  if (tick !== undefined) clearInterval(tick);
  ticks.delete(s);
}

function runTick(s: AppStore): void {
  stopTick(s);
  ticks.set(s, setInterval(() => {
    const p = s.getState().play;
    if (!p || p.paused) { stopTick(s); return; }
    if (p.pos + 1 >= p.len) { stopTick(s); s.set({ play: { key: p.key, pos: 0, len: p.len, paused: true } }); return; }
    s.set({ play: { key: p.key, pos: p.pos + 1, len: p.len, paused: false } });
  }, 1000));
}

/**
 * `toggle`: play, or pause the item that is playing. Starting one item replaces whatever played
 * before. `real` = a media element plays it and reports its own position.
 */
export function togglePlay(s: AppStore, key: string, len: number, real: boolean): void {
  const p = s.getState().play;
  if (p && p.key === key && !p.paused) { stopTick(s); s.set({ play: { key, pos: p.pos, len, paused: true } }); return; }
  const pos = p && p.key === key ? p.pos : 0;
  s.set({ play: { key, pos, len, paused: false } });
  if (real) stopTick(s); else runTick(s);
}

/** A media element reports where it is. */
export function mediaProgress(s: AppStore, key: string, pos: number, len: number): void {
  const p = s.getState().play;
  if (!p || p.key !== key || p.paused) return;
  const total = Number.isFinite(len) && len > 0 ? len : p.len;
  if (Math.abs(p.pos - pos) < 0.2 && p.len === total) return;
  s.set({ play: { key, pos, len: total, paused: false } });
}

/** A media element reached its end: back to the start, paused. */
export function mediaEnded(s: AppStore, key: string): void {
  const p = s.getState().play;
  if (p && p.key === key) s.set({ play: { key, pos: 0, len: p.len, paused: true } });
}

/** A media element could not start or continue: nothing is playing. */
export function mediaStopped(s: AppStore, key: string): void {
  const p = s.getState().play;
  if (p && p.key === key && !p.paused) s.set({ play: { key, pos: p.pos, len: p.len, paused: true } });
}

/** A video's sound goes on: it takes the slot from whatever played (a song stops, another video mutes). */
export function claimSound(s: AppStore, key: string, len: number): void {
  stopTick(s);
  s.set({ play: { key, pos: 0, len, paused: false } });
}

/** A video's sound goes off, or the video could not play: the slot is free again. */
export function releaseSound(s: AppStore, key: string): void {
  if (s.getState().play?.key === key) s.set({ play: null });
}
